import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../AuthProvider";
import { db } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { AlertTriangle, CheckCircle2, Clock3, DollarSign, Eye, FileText } from "lucide-react";

const fmtLKR = new Intl.NumberFormat("en-LK", {
  style: "currency",
  currency: "LKR",
});

function monthLabel(monthKey = "") {
  const [year, month] = String(monthKey || "").split("-").map(Number);
  if (!year || !month) return monthKey || "Unknown month";
  return new Date(year, month - 1, 1).toLocaleString("en-LK", {
    month: "long",
    year: "numeric",
  });
}

function formatDate(value) {
  const date = value?.toDate?.() || value;
  const resolved = date instanceof Date ? date : date ? new Date(date) : null;
  return resolved ? resolved.toLocaleString() : "";
}

function statusTone(status = "") {
  const value = String(status || "").toLowerCase();
  if (value === "confirmed") return "bg-green-100 text-green-700";
  if (value === "disputed") return "bg-rose-100 text-rose-700";
  if (value === "submitted") return "bg-amber-100 text-amber-800";
  return "bg-blue-100 text-blue-700";
}

function slipKindOf(url = "") {
  return String(url || "").toLowerCase().includes(".pdf") ? "pdf" : "image";
}


async function notifyInstructor(instructorUid, payload) {
  if (!instructorUid) return;
  await addDoc(collection(db, "users", instructorUid, "notifications"), {
    userId: instructorUid,
    recipientUid: instructorUid,
    read: false,
    ...payload,
    createdAt: serverTimestamp(),
  });
}

function Modal({ open, onClose, children, max = "max-w-3xl" }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className={`w-full ${max} rounded-3xl border bg-white shadow-2xl overflow-hidden`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

export default function Commissions() {
  const { user } = useAuth();
  const uid = user?.uid || "";
  const [rows, setRows] = useState([]);
  const [reviewState, setReviewState] = useState({
    open: false,
    row: null,
    status: "confirmed",
    note: "",
    busy: false,
  });
  const [slipViewer, setSlipViewer] = useState({
    open: false,
    url: "",
    kind: "image",
    title: "",
  });

  useEffect(() => {
    if (!uid) return;
    const unsub = onSnapshot(
      query(collection(db, "affiliate_settlements"), where("affiliateUid", "==", uid)),
      (snap) => {
        const data = snap.docs
          .map((entry) => ({ id: entry.id, ...entry.data() }))
          .sort((a, b) => String(b.monthKey || "").localeCompare(String(a.monthKey || "")));
        setRows(data);
      },
    );
    return () => unsub();
  }, [uid]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.total += Number(row.totalCommission || 0);
        if ((row.status || "") === "submitted") acc.pending += Number(row.totalCommission || 0);
        if ((row.status || "") === "confirmed") acc.confirmed += Number(row.totalCommission || 0);
        if ((row.status || "") === "disputed") acc.disputed += Number(row.totalCommission || 0);
        return acc;
      },
      { total: 0, pending: 0, confirmed: 0, disputed: 0 },
    );
  }, [rows]);

  async function submitReview() {
    if (!reviewState.row || reviewState.busy) return;
    setReviewState((prev) => ({ ...prev, busy: true }));
    try {
      await updateDoc(doc(db, "affiliate_settlements", reviewState.row.id), {
        status: reviewState.status,
        affiliateNote: reviewState.note.trim() || null,
        affiliateReviewedAt: serverTimestamp(),
        affiliateReviewedBy: uid,
        updatedAt: serverTimestamp(),
      });
      await notifyInstructor(reviewState.row.instructorUid, {
        status: reviewState.status,
        type: "affiliate_settlement",
        settlementId: reviewState.row.id,
        message: `Affiliate marked the ${monthLabel(reviewState.row.monthKey)} payout as ${reviewState.status}.`,
      });
      setReviewState({
        open: false,
        row: null,
        status: "confirmed",
        note: "",
        busy: false,
      });
    } catch (error) {
      console.error("affiliate review failed", error);
      alert("Could not update the settlement. Please try again.");
      setReviewState((prev) => ({ ...prev, busy: false }));
    }
  }

  return (
    <div className="min-h-[90vh] bg-gradient-to-br from-blue-50 via-purple-50 to-blue-100 py-8 md:pt-24">
      <div className="mx-auto max-w-6xl space-y-6 px-4">
        <div className="rounded-3xl border bg-white p-6 shadow-sm">
          <h1 className="text-2xl font-black tracking-tight md:text-3xl">
            Commission Settlements
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-gray-600 md:text-base">
            Review what each instructor paid you for the month, open the slip,
            and confirm or dispute it from here.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Total
            </div>
            <div className="mt-2 text-2xl font-black">{fmtLKR.format(totals.total)}</div>
          </div>
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Waiting Review
            </div>
            <div className="mt-2 text-2xl font-black">{fmtLKR.format(totals.pending)}</div>
          </div>
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Confirmed
            </div>
            <div className="mt-2 text-2xl font-black">{fmtLKR.format(totals.confirmed)}</div>
          </div>
          <div className="rounded-3xl border bg-white p-5 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Disputed
            </div>
            <div className="mt-2 text-2xl font-black">{fmtLKR.format(totals.disputed)}</div>
          </div>
        </div>

        <div className="space-y-4">
          {rows.length === 0 ? (
            <div className="rounded-3xl border bg-white p-10 text-center text-gray-500 shadow-sm">
              No settlement records yet.
            </div>
          ) : (
            rows.map((row) => (
              <div key={row.id} className="rounded-3xl border bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-xl font-black">
                        {row.instructorSnapshot?.name || row.instructorSnapshot?.slug || "Instructor"}
                      </h2>
                      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${statusTone(row.status)}`}>
                        {row.status || "submitted"}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-2">
                        <Clock3 /> {monthLabel(row.monthKey)}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <DollarSign /> {fmtLKR.format(Number(row.totalCommission || 0))}
                      </span>
                      <span className="inline-flex items-center gap-2">
                        <FileText /> {Number(row.orderCount || 0)} sales
                      </span>
                    </div>
                    {row.notes && (
                      <div className="rounded-2xl bg-blue-50 px-4 py-3 text-sm text-blue-800">
                        Instructor note: {row.notes}
                      </div>
                    )}
                    {row.affiliateNote && (
                      <div className="rounded-2xl bg-gray-100 px-4 py-3 text-sm text-gray-700">
                        Your note: {row.affiliateNote}
                      </div>
                    )}
                    <div className="text-xs text-gray-500">
                      Submitted: {formatDate(row.submittedAt)}
                      {row.affiliateReviewedAt ? ` • Reviewed: ${formatDate(row.affiliateReviewedAt)}` : ""}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {row.slipUrl && (
                      <button
                        type="button"
                        className="rounded-2xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
                        onClick={() =>
                          setSlipViewer({
                            open: true,
                            url: row.slipUrl,
                            kind: row.slipType || slipKindOf(row.slipUrl),
                            title: `${row.instructorSnapshot?.name || "Instructor"} slip`,
                          })
                        }
                      >
                        <Eye className="inline-block" /> Open slip
                      </button>
                    )}
                    <button
                      type="button"
                      className="rounded-2xl bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700"
                      onClick={() =>
                        setReviewState({
                          open: true,
                          row,
                          status: "confirmed",
                          note: row.affiliateNote || "",
                          busy: false,
                        })
                      }
                    >
                      Confirm / dispute
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Modal
        open={reviewState.open}
        onClose={() =>
          !reviewState.busy &&
          setReviewState({ open: false, row: null, status: "confirmed", note: "", busy: false })
        }
      >
        <div className="space-y-5">
          <div>
            <h3 className="text-xl font-black">Review settlement</h3>
            <p className="mt-1 text-sm text-gray-600">
              Confirm that you received the correct amount or mark it as disputed.
            </p>
          </div>

          <div className="rounded-2xl bg-gray-50 p-4 text-sm text-gray-700">
            <div>Instructor: <span className="font-semibold">{reviewState.row?.instructorSnapshot?.name || "Instructor"}</span></div>
            <div className="mt-1">Month: <span className="font-semibold">{monthLabel(reviewState.row?.monthKey)}</span></div>
            <div className="mt-1">Amount: <span className="font-semibold">{fmtLKR.format(Number(reviewState.row?.totalCommission || 0))}</span></div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <button
              type="button"
              className={`rounded-2xl border px-4 py-3 text-left font-semibold ${reviewState.status === "confirmed" ? "border-green-500 bg-green-50 text-green-700" : "hover:bg-gray-50"}`}
              onClick={() => setReviewState((prev) => ({ ...prev, status: "confirmed" }))}
            >
              <CheckCircle2 className="mb-2" /> Received correctly
            </button>
            <button
              type="button"
              className={`rounded-2xl border px-4 py-3 text-left font-semibold ${reviewState.status === "disputed" ? "border-rose-500 bg-rose-50 text-rose-700" : "hover:bg-gray-50"}`}
              onClick={() => setReviewState((prev) => ({ ...prev, status: "disputed" }))}
            >
              <AlertTriangle className="mb-2" /> Wrong amount / not received
            </button>
          </div>

          <label className="block text-sm font-semibold text-gray-700">
            Note
            <textarea
              rows={4}
              className="mt-2 w-full rounded-2xl border px-4 py-3 text-sm outline-none focus:border-blue-500"
              placeholder="Add a note for the instructor"
              value={reviewState.note}
              onChange={(event) =>
                setReviewState((prev) => ({ ...prev, note: event.target.value }))
              }
            />
          </label>

          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="rounded-2xl border px-4 py-2 font-semibold hover:bg-gray-50"
              onClick={() =>
                setReviewState({ open: false, row: null, status: "confirmed", note: "", busy: false })
              }
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-2xl bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={submitReview}
              disabled={reviewState.busy}
            >
              {reviewState.busy ? "Saving..." : "Save review"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={slipViewer.open}
        onClose={() => setSlipViewer({ open: false, url: "", kind: "image", title: "" })}
        max="max-w-5xl"
      >
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-black">{slipViewer.title || "Payment slip"}</h3>
              <p className="mt-1 text-sm text-gray-600">Review the uploaded payout proof.</p>
            </div>
            <a
              href={slipViewer.url}
              target="_blank"
              rel="noreferrer"
              className="rounded-2xl border px-4 py-2 text-sm font-semibold hover:bg-gray-50"
            >
              Open in new tab
            </a>
          </div>
          <div className="overflow-hidden rounded-3xl border bg-gray-50">
            {slipViewer.kind === "pdf" ? (
              <iframe title={slipViewer.title || "Slip PDF"} src={slipViewer.url} className="h-[70vh] w-full" />
            ) : (
              <img src={slipViewer.url} alt={slipViewer.title || "Slip"} className="max-h-[70vh] w-full object-contain" />
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}
